
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ProgressCard
 *
 * @file ProgressCard.jsx
 * @author Mohamed yasar arabath M
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ProgressCardRoot,
  ProgressCardContent,
  ProgressCardHeader,
  ProgressCardTitle,
  ProgressCardBody,
  ProgressStatsSection,
  ProgressItemsList,
  ProgressItem,
  StatusDotContainer,
  DotBase,
  StatusLabel,
  PercentageText,
  ProgressCardFooter,
  DateSection,
  DateLabel,
  BadgeWrapper
} from './styles/ProgressCardStyle';
import CircularProgressBarWidget from '../common/CircularProgressBarWidget';
import { COMPLETED } from '../../common/constants';
import { BadgeTemplate } from '../tabular/components/Template';
import Utils from '../../common/utils';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';

const ProgressCard = ({
  title,
  segments,
  footerContent,
  progressSubLabel,
  comprehensionData,
  showBadge,
  tooltipSegments,
  isPercentage,
  enableDotIndicators
}) => {
  const [progressCardActive, setProgressCardActive] = useState(null);

  const completedCount = segments.reduce((acc, seg) => {
    let value = acc;
    if (Utils.localStringCompare(seg.type, COMPLETED)) {
      value += seg.value;
    }

    return value;
  }, 0);

  const tooltipContent = tooltipSegments?.length ? (
    <StatusTemplate segments={tooltipSegments} isPercentage={isPercentage} />
  ) : null;

  const onProgressCardMouseEnter = (segment) => {
    setProgressCardActive(segment.color);
  };

  const onProgressCardMouseLeave = () => {
    setProgressCardActive(null);
  };

  const renderBadgeTemplate = () => (
    <BadgeWrapper>
      <BadgeTemplate
        topics={null}
        status={comprehensionData.status}
        label={comprehensionData.comprehension}
        showIcon={comprehensionData.showIcon}
      />
    </BadgeWrapper>
  );

  const renderBadge = () => {
    if (!comprehensionData || !showBadge) {
      return null;
    }

    return comprehensionData?.showTooltip ?
      <HtmlTooltip title={tooltipContent} placement="bottom" arrow>
        {renderBadgeTemplate(comprehensionData)}
      </HtmlTooltip>
      : renderBadgeTemplate(comprehensionData);
  };

  return (
    <ProgressCardRoot>
      <ProgressCardHeader>
        <ProgressCardTitle>{title}</ProgressCardTitle>

        {/* Right side comprehension badge with tooltip */}
        {renderBadge()}
      </ProgressCardHeader>
      <ProgressCardContent>
        <CircularProgressBarWidget
          segments={segments}
          progressLabel={completedCount}
          progressSubLabel={progressSubLabel}
          onMouseEnter={onProgressCardMouseEnter}
          onMouseLeave={onProgressCardMouseLeave}
          activeColor={progressCardActive}
          unControlledTooltip
          enableDotIndicators={enableDotIndicators}
        />
        <ProgressCardBody>
          <ProgressStatsSection>
            <ProgressItemsList>
              {segments.map((seg) => (
                <ProgressItem
                  key={seg.label}
                  onMouseEnter={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onProgressCardMouseEnter(seg);
                  }}
                  onMouseLeave={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onProgressCardMouseLeave();
                  }}
                >
                  <StatusDotContainer>
                    <DotBase
                      isActive={progressCardActive === seg.color}
                      style={{ backgroundColor: seg.color, color: seg.color }}
                    />
                  </StatusDotContainer>
                  <StatusLabel>{seg.label}:</StatusLabel>
                  <PercentageText>{seg.value}</PercentageText>
                </ProgressItem>
              ))}
            </ProgressItemsList>
          </ProgressStatsSection>
        </ProgressCardBody>
      </ProgressCardContent>
      {footerContent ?
        (
          <ProgressCardFooter>
            {footerContent.map((item) => (
              <DateSection key={item.label}>
                <DateLabel>{item.label}</DateLabel>
                <DateLabel>{item.value}</DateLabel>
              </DateSection>
            ))}
          </ProgressCardFooter>
        ) : null}
    </ProgressCardRoot>
  );
};

ProgressCard.defaultProps = {
  footerContent: null,
  progressSubLabel: null,
  comprehensionData: null,
  showBadge: false,
  tooltipSegments: [],
  isPercentage: true,
  enableDotIndicators: false
};

ProgressCard.propTypes = {
  title: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      type: PropTypes.string
    })
  ).isRequired,
  footerContent: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  progressSubLabel: PropTypes.string,
  comprehensionData: PropTypes.shape({
    comprehension: PropTypes.string.isRequired,
    completedOnTime: PropTypes.number.isRequired,
    completedPastDue: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    showIcon: PropTypes.bool.isRequired,
    showTooltip: PropTypes.bool.isRequired
  }),
  showBadge: PropTypes.bool,
  tooltipSegments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ),
  isPercentage: PropTypes.bool,
  enableDotIndicators: PropTypes.bool
};

export default ProgressCard;
